import type { RfqInvitation } from "../../domain/rfq-types";
import { localDateTimeToUtc } from "../../rfq-timezone";

export function rfqCountLabel(value: unknown) {
  return typeof value === "number" && Number.isSafeInteger(value) && value >= 0
    ? String(value)
    : "Doğrulanmadı";
}

export function rfqDaysLabel(value: unknown) {
  const label = rfqCountLabel(value);
  return label === "Doğrulanmadı" ? label : `${label} gün`;
}

export function rfqMoneyLabel(value: unknown, currency: unknown) {
  if (typeof currency !== "string" || !/^[A-Z]{3}$/.test(currency)) return "Doğrulanmadı";
  if (typeof value !== "number" && (typeof value !== "string" || !/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?$/.test(value.trim()))) return "Doğrulanmadı";
  const amount = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(amount)) return "Doğrulanmadı";
  return new Intl.NumberFormat("tr-TR", { style: "currency", currency }).format(amount);
}

export const statusLabels: Record<string, string> = {
  DRAFT: "Taslak",
  PUBLISHED: "Yayımlandı",
  AWAITING_BIDS: "Teklif bekliyor",
  CLOSED: "Kapandı",
  UNSEALED: "Kapalı teklifler açıldı",
  CANCELLED: "İptal edildi",
};

export const methodLabels: Record<string, string> = {
  OPEN: "Açık teklif",
  SEALED: "Kapalı teklif",
  SOLE_SOURCE: "Tek kaynak",
  EMERGENCY: "Acil alım",
};

export const eligibilityReasonLabels: Record<string, string> = {
  SUPPLIER_NOT_ACTIVE: "Tedarikçi aktif değil",
  SUPPLIER_QUALIFICATION_SCOPE_MISSING:
    "Tesis ve ürün grubu kalite kapsamı eksik",
  QUALIFICATION_NOT_APPROVED: "Resmî kalite onayı yok",
  MANDATORY_DOCUMENT_MISSING: "Zorunlu kalite belgesi eksik",
  DOCUMENT_PENDING_SCAN: "Belgenin güvenlik kontrolü bekleniyor",
  DOCUMENT_MALICIOUS: "Belge güvenlik nedeniyle engellendi",
  DOCUMENT_EXPIRED: "Zorunlu belgenin süresi doldu",
  CRITICAL_CAPA_OPEN: "Kritik düzeltici faaliyet açık",
  CONDITION_MAX_QUANTITY_NOT_MET: "Şartlı onayın miktar sınırı aşılıyor",
};

export const eligibilityReasonLabel = (code: string) =>
  eligibilityReasonLabels[code] ?? "Tedarikçi uygunluk koşulu sağlanmadı";

export const rfqUiMessage = (value: unknown, fallback: string) => {
  const message = value instanceof Error ? value.message.trim() : "";
  if (!message || /^[A-Z0-9_]+$/.test(message)) return fallback;
  return message.replaceAll("RFQ", "Teklif Talebi");
};

export const invitationStatusLabels: Record<string, string> = {
  QUEUED: "Kuyrukta",
  SENT: "Gönderildi",
  FAILED: "Gönderim başarısız",
  DEAD: "Müdahale gerekiyor",
  REVOKED: "İptal edildi",
  PENDING: "Bekliyor",
  PROCESSING: "İşleniyor",
};

export const invitationSummary = (item: RfqInvitation) =>
  `${invitationStatusLabels[item.status] ?? "Bilinmiyor"} · ${invitationStatusLabels[item.queueStatus ?? ""] ?? "Bilinmiyor"}`;

export const invitationErrorMessage = (value: unknown) => {
  const message = value instanceof Error ? value.message : "";
  if (/403|yetki|forbidden/i.test(message))
    return "Bu davet işlemi için yetkiniz bulunmuyor.";
  if (/404|bulunamadı|not found/i.test(message))
    return "Davet kaydı bulunamadı. Listeyi yenileyip tekrar deneyin.";
  if (/409|conflict|idempotency|değişti/i.test(message))
    return "Davet başka bir işlemle değişti. Güncel durumu yükleyip tekrar deneyin.";
  return "Davet işlemi şu anda tamamlanamadı. Lütfen tekrar deneyin.";
};

export const invitationWriteErrorMessage = (value: unknown) => {
  const code = value instanceof Error && "code" in value && typeof value.code === "string" ? value.code : "";
  if (code === "NETWORK_ERROR" || code === "INVALID_API_RESPONSE" || /^HTTP_5/.test(code)) {
    return "Davet işleminin sonucu doğrulanamadı. Tekrar göndermeden önce davet listesini yenileyip durumunu kontrol edin.";
  }
  return invitationErrorMessage(value);
};

export const formatInvitationDate = (value?: string) =>
  value
    ? new Intl.DateTimeFormat("tr-TR", {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "Kayıt yok";

export function rfqDeadlinePreview(form: { deadlineAt: string; timezone: string }) {
  let deadlinePreview =
    "Yerel tarih ve saat seçildiğinde UTC karşılığı gösterilir.";
  if (form.deadlineAt) {
    try {
      deadlinePreview = `${form.timezone} yerel saati · ${localDateTimeToUtc(form.deadlineAt, form.timezone)} UTC`;
    } catch {
      deadlinePreview =
        "Seçilen yerel saat ve saat dilimi birlikte doğrulanamadı.";
    }
  }
  return deadlinePreview;
}

export function rfqReviewDeadline(form: { deadlineAt: string; timezone: string }) {
  const match = form.deadlineAt.match(
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/,
  );
  if (!match) return "Belirtilmedi";
  const [, year, month, day, hour, minute] = match;
  const date = new Intl.DateTimeFormat("tr-TR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(
    new Date(
      Date.UTC(Number(year), Number(month) - 1, Number(day), 12, 0, 0),
    ),
  );
  return `${date}, ${hour}:${minute} (${form.timezone})`;
}
